import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { PlaceOutletContextType } from "../home/PlacesHome";
import { Lead, NotificationType } from 'shared/topchef.types';
import placesApi from 'shared/topchef-api-places';
import Loading from 'pages/shared/loading/Loading'
import { NotificationContext } from 'shared/NotificationContext';
import { useErrorBoundary } from 'react-error-boundary';
import PublicLeadContent from 'pages/shared/public-lead-content/PublicLeadContent';
import { useTranslation } from 'react-i18next';

const LeadToCloseDetails = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [loggedInPlace] = useOutletContext<PlaceOutletContextType>();

  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;

  const params = useParams();
  const leadId: string = params.leadId || '';

  const [isReady, setReady] = useState<boolean>(false);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);

  const { showBoundary } = useErrorBoundary();

  interface billLeadForm {
    billContent: File | null;
    amount: number;
  }
    
  const [billFormData, setBillFormData] = useState<billLeadForm>({
    billContent: null,
    amount: 0,
  });

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps

    async function loadLead() {
      try {
        const lead: Lead = await placesApi.loadLeadToClose(loggedInPlace!.id, leadId);
        setCurrentLead(lead);
        setReady(true);
      } catch (error: any) { 
        const newError = new Error(t('apiErrors.loadLeadToClose', { details: error.message }) as string);
        triggerNotification({type: NotificationType.error, message : `${newError.message}`}); 
        showBoundary(newError);
      } 
    }

    if (isReady) {
      return;
    }

    loadLead();

  }, [currentLead, isReady, leadId, loggedInPlace, showBoundary, triggerNotification]); // eslint-disable-line react-hooks/exhaustive-deps


  const shareBill = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await placesApi.shareBill(loggedInPlace!.id, leadId, billFormData.billContent!, billFormData.amount!);
      triggerNotification({type: NotificationType.information, message : t('bill.billSharedSuccess')}); 
      navigate('/places/leads/to-close');
    } catch (error: any) {
      const newError = new Error(t('apiErrors.shareBill', { details: error.message }) as string);
      triggerNotification({type: NotificationType.error, message : `${newError.message}`}); 
      showBoundary(newError);
    }

  };

  const handleInputEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const name = event.target.name;

    let value : string | File;
    if (event.target.files == null) {
      value = event.target.value
    }
    else {
      value = event.target.files[0];
    }

    setBillFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };


  if (!isReady) {
    return (
      <Loading />
    );
  }

  if (currentLead!.status === 'signed') {
    return (
      <div className="container page-briefs-single-details p-5 col-md-6 overflow-auto">
        
        <div className="col-md-6 brief-details-col">
          <PublicLeadContent publicLead={currentLead!} />
        </div>

        <div className="col-md-6 invoice-upload-col p-5 overflow-auto row d-flex align-items-center justify-content-center">
          <h3 className="mb-3 text-center">{t('bill.sendBill')}</h3>
          <div id="event_details_form" className="col-md-9 text-center card mt-2">
            <form id="invoice_details_form" className="d-flex row justify-content-center p-3 pb-4" onSubmit={shareBill}>
              <div className="mb-4 col-md-12 text-center justify-content-center">
                <label htmlFor="invoice_price" className="form-label">{t('bill.billAmount')}</label>
                  <input id="invoice_price" name='amount' min='1' onChange={handleInputEventChange} type="number" className="col-md-6 text-center" placeholder={String(t('leadEditor.amountExVat'))} required/>
              </div>
              <div className="mb-4 row">
                <div className="col-md-12">
                  <label className="col-md-12" htmlFor="bill-file-input">
                    {t('bill.dragDropBill')}
                    <strong className="m-2">{t('bill.or')}</strong>
                    <input id="bill-file-input" name="billContent" type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleInputEventChange} data-testid="bill-file-input" required/>
                  </label>
                </div>     
              </div>                  
              <div className="text-center">
                <button type="submit" id="save_brief_details" className="btn btn-primary m-3" data-testid="share-bill-button">{t('bill.send')}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }


  function getLeadStatusDisplayMessage() {
    if (currentLead!.status === 'billed') {
      return t('bill.billValidating');
    }
    if (currentLead!.status === 'verified') {
      return t('bill.billValidated');
    }
    return "";
  }

  return (
    <div className="container p-2 page-briefs-single-details col-md-6 overflow-auto">
      <div id="lead_sidebar" className="d-flex">
        <ul className="nav nav-pills flex-column mb-auto">
          <li className="nav-item"> <a href="/places/share-a-lead" type="button" className="btn btn-outline-primary nav-link add-brief-link">{t('common.addBrief')}</a></li>
        </ul>
      </div>
       
      <PublicLeadContent publicLead={currentLead!} />

      <div className="text-center">
        <div className="alert alert-success brief-status-details" role="alert">
          {getLeadStatusDisplayMessage()}
        </div>     
      </div>
    </div>
  );

}


export default LeadToCloseDetails;
